import {
  LINES_OF_WORK,
  findLineOfWork,
  normalizeLineOfWork,
  type PartnerCategory,
} from "@roof-hawk/core";
import { useRef } from "react";

/**
 * "Plumber", "Electrician": the specific line of work on a card (ADR 0262).
 *
 * A text box with the common ones offered as you type, rather than a fixed
 * list, so a line of work nobody thought of can still go in. Leaving the box
 * tidies what was typed: one the list knows is saved the list's way, and
 * moves the category with it, so the texting side never disagrees with it.
 * Only leaving the box with a different word does that, never a keystroke on
 * the way.
 */
export function LineOfWorkField({
  id,
  value,
  onChange,
  onCategory,
  lowConfidence = false,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  /** Called with the category a listed line of work belongs to. */
  onCategory?: (category: PartnerCategory) => void;
  lowConfidence?: boolean;
}) {
  const listId = `${id}-options`;
  // The settled word as it stood when the box was entered.
  const onEntry = useRef<string | null>(null);
  return (
    <div className={`field${lowConfidence ? " tocheck" : ""}`}>
      <label htmlFor={id}>Line of work</label>
      <input
        id={id}
        list={listId}
        value={value}
        placeholder="Plumber, Electrician, Realtor…"
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => {
          onEntry.current = normalizeLineOfWork(value);
        }}
        onBlur={() => {
          // The category follows the settled word only. Read on every
          // keystroke, a short alias inside a longer word ("ac" while typing
          // "Accountant") would set a category nobody chose, and with the
          // network off nobody can see it to put it right.
          const settled = normalizeLineOfWork(value);
          onChange(settled ?? "");
          // And only when the word has changed: passing through the box
          // must not undo a category somebody picked by hand.
          if (settled === onEntry.current) return;
          const listed = findLineOfWork(settled);
          if (listed) onCategory?.(listed.category);
        }}
      />
      <datalist id={listId}>
        {LINES_OF_WORK.map((entry) => (
          <option key={entry.label} value={entry.label} />
        ))}
      </datalist>
    </div>
  );
}
